import moment from "moment"
import { BookerAttendeeInput } from "../../generated/graphql"
import { attendeeCheck } from "./checkAttendee"

/**
 * Dubletten-Erkennung für Teilnehmer desselben Buchers (sc-3066).
 *
 * Match-Kriterium: normalisierter Vorname + Nachname und exaktes Geburtsdatum
 * (nur Datumsanteil). Bewusst eng gefasst, weil das Ergebnis die Neuanlage
 * hart blockiert — False Positives würden legitime Anlagen verhindern.
 */

// Normalisiert Namensbestandteile: trimmen, Kleinschreibung, Mehrfach-Leerzeichen zusammenfassen
export const normalizeNamePart = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase().replace(/\s+/g, " ")

// Reduziert ein Geburtsdatum auf den reinen Datumsanteil (YYYY-MM-DD).
// ISO-Strings werden per String-Präfix gekürzt statt per moment geparst, damit
// UTC-Mitternacht ("2015-06-15T00:00:00.000Z") in westlichen Zeitzonen nicht
// auf den Vortag kippt.
export const toDateOnly = (value: string | Date | null | undefined): string | undefined => {
    if (value === null || value === undefined || value === "") return undefined
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined
}

export type DuplicateAttendeeMatch = {
    attendee: BookerAttendeeInput
    // Schreibweise folgt bewusst attendeeCheck (Bestandscode)
    allreadyBooked: boolean
}

type AttendeeIdentity = { firstname?: string | null; lastname?: string | null; birthday?: string | Date | null }

/**
 * Vergleicht die Identitätsdaten (normalisierter Name + Geburtsdatum) zweier
 * Teilnehmer. Wird im Edit-Modus genutzt, um den Dubletten-Guard nur bei
 * GEÄNDERTER Identität scharf zu schalten — sonst könnte ein Bestands-Klon
 * (Import-Altlast) nie bearbeitet werden, weil sein Zwilling immer matcht.
 */
export const hasSameIdentity = (a: AttendeeIdentity, b: AttendeeIdentity): boolean =>
    normalizeNamePart(a.firstname) === normalizeNamePart(b.firstname) &&
    normalizeNamePart(a.lastname) === normalizeNamePart(b.lastname) &&
    toDateOnly(a.birthday) === toDateOnly(b.birthday)

/**
 * Sucht unter den Bestands-Teilnehmern eines Buchers ein Duplikat des Kandidaten.
 *
 * - Fail-open: Bei Teileingabe (leerer Name / fehlendes Geburtsdatum) oder
 *   leerer/fehlender Teilnehmerliste wird `undefined` geliefert — die Neuanlage
 *   darf nie blockiert sein, nur weil Daten (noch) fehlen.
 * - `excludeId` nimmt im Edit-Modus den bearbeiteten Teilnehmer selbst vom Match aus.
 * - Bei mehreren Matches (Import-Altlast) wird der erste noch buchbare Teilnehmer
 *   bevorzugt, damit der "Weiter mit ..."-CTA nicht auf einen bereits in diesen
 *   Kurs gebuchten Klon zeigt.
 */
export const findDuplicateAttendee = (
    attendees: BookerAttendeeInput[] | null | undefined,
    candidate: { firstname?: string | null; lastname?: string | null; birthday?: string | Date | null },
    options?: { excludeId?: string; courseId?: string }
): DuplicateAttendeeMatch | undefined => {
    const firstname = normalizeNamePart(candidate.firstname)
    const lastname = normalizeNamePart(candidate.lastname)
    const birthday = toDateOnly(candidate.birthday)
    if (!attendees?.length || !firstname || !lastname || !birthday) return undefined

    const matches = attendees.filter(
        (attendee) =>
            attendee.id !== options?.excludeId &&
            normalizeNamePart(attendee.firstname) === firstname &&
            normalizeNamePart(attendee.lastname) === lastname &&
            toDateOnly(attendee.birthday) === birthday
    )
    if (matches.length === 0) return undefined

    const enriched = matches.map((attendee) => ({
        attendee,
        allreadyBooked: attendeeCheck(attendee, options?.courseId).allreadyBooked,
    }))

    return enriched.find((match) => !match.allreadyBooked) ?? enriched[0]
}
